/*
 * BD hybrid marquee: native horizontal scroll + auto-advance.
 *
 * Markup contract:
 *   <div data-bd-marquee data-speed="0.4" data-resume-ms="1500">
 *     <div data-bd-marquee-track>  ← duplicated content inside
 *       <article>…</article>
 *       <article aria-hidden="true">…</article>  ← second pass
 *     </div>
 *   </div>
 *
 * Behavior:
 *   - Viewport is a native horizontal scroller (overflow-x: auto).
 *   - rAF loop advances scrollLeft by `speed` px/frame.
 *   - Pause on pointerdown / mouseenter / wheel / keyboard scroll.
 *   - Resume `resumeMs` after the last interaction ended.
 *   - Seamless wrap at halfWidth (track is duplicated 1×, so half = one full set).
 *   - Respects prefers-reduced-motion (no auto-advance; native scroll only).
 */

(function () {
  'use strict';

  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    var nodes = document.querySelectorAll('[data-bd-marquee]');
    for (var i = 0; i < nodes.length; i++) setup(nodes[i]);
  }

  function setup(viewport) {
    var track = viewport.querySelector('[data-bd-marquee-track]') || viewport.firstElementChild;
    if (!track) return;

    var speed = parseFloat(viewport.getAttribute('data-speed')) || 0.4;
    var resumeMs = parseFloat(viewport.getAttribute('data-resume-ms')) || 1500;

    // Take over from the CSS keyframe animation
    track.style.animation = 'none';
    track.style.transform = 'none';

    var userInteracting = false;
    var lastInteractionAt = 0;
    // `pos` is the JS-side accumulator (full float precision). `viewport.scrollLeft`
    // may be quantized to device-pixel steps (e.g. 0.5px on 2x displays), so reading
    // it back loses sub-pixel progress and stalls motion at speeds < the quantum.
    // We accumulate in `pos` and only read scrollLeft to detect user interaction.
    var pos = viewport.scrollLeft;
    var lastAutoScrollLeft = pos;
    var halfWidth = track.scrollWidth / 2;

    function remeasure() { halfWidth = track.scrollWidth / 2; }

    function markInteraction() { lastInteractionAt = performance.now(); }

    function tick(now) {
      // Detect external scroll (user drag, momentum, wheel) by comparing
      // expected vs actual position. If they diverge, treat as interaction
      // and resync the accumulator to whatever the user scrolled to.
      if (Math.abs(viewport.scrollLeft - lastAutoScrollLeft) > 0.5) {
        markInteraction();
        pos = viewport.scrollLeft;
        lastAutoScrollLeft = viewport.scrollLeft;
      }

      if (!userInteracting && now - lastInteractionAt > resumeMs && halfWidth > 0) {
        pos += speed;
        if (pos >= halfWidth) pos -= halfWidth;
        viewport.scrollLeft = pos;
        // Browser may quantize fractional scrollLeft; remember what landed so
        // the diff check above doesn't false-trigger next frame.
        lastAutoScrollLeft = viewport.scrollLeft;
      }

      requestAnimationFrame(tick);
    }

    // Pointer / touch interaction
    viewport.addEventListener('pointerdown', function () {
      userInteracting = true;
      markInteraction();
    });
    window.addEventListener('pointerup', function () {
      if (userInteracting) {
        userInteracting = false;
        markInteraction();
      }
    });
    window.addEventListener('pointercancel', function () {
      if (userInteracting) {
        userInteracting = false;
        markInteraction();
      }
    });

    // Desktop hover pause
    viewport.addEventListener('mouseenter', function () { userInteracting = true; });
    viewport.addEventListener('mouseleave', function () {
      userInteracting = false;
      markInteraction();
    });

    // Wheel / keyboard interaction
    viewport.addEventListener('wheel', markInteraction, { passive: true });
    viewport.addEventListener('keydown', markInteraction);

    // Resize → remeasure
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(remeasure, 150);
    });

    // Re-measure once images settle (they affect track width)
    var imgs = track.querySelectorAll('img');
    var pending = 0;
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) {
        pending++;
        imgs[i].addEventListener('load', function () {
          pending--;
          if (pending === 0) remeasure();
        }, { once: true });
        imgs[i].addEventListener('error', function () {
          pending--;
          if (pending === 0) remeasure();
        }, { once: true });
      }
    }

    if (!prefersReduced) {
      requestAnimationFrame(tick);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
